import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Routes, Route, useLocation, useParams, Navigate } from 'react-router-dom';
import TripsList from './routes/TripsList';
import AppShell from './components/AppShell';
import ErrorBoundary from './components/ErrorBoundary';
import ToastHost from './components/ToastHost';

// Only the `/` landing (TripsList) + the shared shell load eagerly. Every
// other route is code-split, so the initial bundle carries React, the
// router, the shell, and the trips list — nothing else. The heavy
// detail-section / editor components (CatalogDetail, Planner, Browse, …)
// download only when their route is actually visited; whatever route you
// deep-link into pulls just its own chunk plus the shell.
const NewTripWizard = lazy(() => import('./routes/NewTripWizard'));
const Overview = lazy(() => import('./routes/Overview'));
const Browse = lazy(() => import('./routes/Browse'));
const CatalogDetail = lazy(() => import('./routes/CatalogDetail'));
const Days = lazy(() => import('./routes/Days'));
const Reservations = lazy(() => import('./routes/Reservations'));
const Budget = lazy(() => import('./routes/Budget'));
const Meals = lazy(() => import('./routes/Meals'));
const Experiences = lazy(() => import('./routes/Experiences'));
const Showcase = lazy(() => import('./routes/Showcase'));
const SharedShowcase = lazy(() => import('./routes/Showcase').then((m) => ({ default: m.SharedShowcase })));
const GlobalReservations = lazy(() => import('./routes/GlobalReservations'));
// Catalog-wide dining-pedigree table (ryokan/auberge kitchens + notable
// hotel restaurants) — global reference page, not trip-scoped.
const Dining = lazy(() => import('./routes/Dining'));
// Tokyo→Fuji corridor lodging explorer — sibling research page to /dining
// (shared ResearchTabs strip); facets from src/data/corridor-lodging.json.
const CorridorLodging = lazy(() => import('./routes/CorridorLodging'));
const Planner = lazy(() => import('./routes/Planner'));
const GuestLanding = lazy(() => import('./routes/GuestLanding'));
// App feedback to Jake's queue in WaageWu (POST /api/me/feedback).
const Feedback = lazy(() => import('./routes/Feedback'));

const TripTransit = lazy(() => import('./routes/TripTransit'));
// Full-bleed map mode (the /maps prototype look, on the shared map-mode
// foundation) at /trip/:tripId/map-mode. Lazy — pulls in mapbox-gl.
const MapMode = lazy(() => import('./routes/MapMode'));
// NOTE: the standalone /maps prototype is still served React-free by the
// Worker (handleMapsApp → /maps-app/index.html); the React map mode above is
// the migration target that will eventually replace it.
import { getMe, ApiError, ME_LS } from './lib/api';
import { GuestContext, guestSessionFor } from './lib/guest';

// Decision short link — /d/{tripId}/{decisionId} is the texted-to-a-partner
// form. On iOS with the mobile app installed, the universal link (AASA at
// /.well-known/apple-app-site-association, served by the Worker) opens the
// app instead; this browser fallback lands in map mode with the decision's
// fullscreen module open.
function DecisionShortLink() {
  const { tripId, decisionId } = useParams();
  return (
    <Navigate
      to={`/trip/${tripId}/map-mode?decision=${encodeURIComponent(decisionId ?? '')}&view=full`}
      replace
    />
  );
}

function MapRedirect() {
  const { tripId } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/trip/${tripId}/map-mode${search}`} replace />;
}

function NotFound() {
  return (
    <AppShell>
      <div className="empty-state">
        <h2>Not found</h2>
        <p>That page doesn't exist.</p>
        <p>
          <a href="/">Go to your trips</a>
        </p>
      </div>
    </AppShell>
  );
}

// Shown when the visitor isn't authenticated. The bare host is no longer
// gated at the Cloudflare Access edge — the Worker serves a rich-preview
// landing at `/` for anonymous visitors, but a deep link (e.g. /new or a
// shared /trip/:id/planner) loads the SPA directly. This screen catches
// that case and routes them through `/signin`, the one Access-gated path,
// preserving where they were headed via `?next=`.
function SignInScreen() {
  const loc = useLocation();
  const next = encodeURIComponent(loc.pathname + loc.search);
  return (
    <AppShell>
      <div className="empty-state">
        <h2>Sign in to continue</h2>
        <p>Your trips are private. Sign in to view and edit them.</p>
        <p>
          <a className="btn btn-primary" href={`/signin?next=${next}`}>
            Sign in
          </a>
        </p>
      </div>
    </AppShell>
  );
}

// Last successful /api/me, cached so a returning member renders optimistically
// instead of blocking first paint on the probe. We only persist the trip-id
// list (all AuthGate reads from /api/me) — never anything sensitive; the
// route content itself always comes from fresh, credentialed API calls that
// 401 server-side if the session is actually gone.
const ME_CACHE_KEY = 'jg.web.me.v1';
interface CachedMe {
  tripIds: string[];
  at: number;
}
function readCachedMe(): CachedMe | null {
  try {
    const raw = localStorage.getItem(ME_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedMe;
    if (Array.isArray(parsed?.tripIds) && typeof parsed?.at === 'number') {
      return parsed;
    }
  } catch {
    // corrupted / storage unavailable — fall back to the blocking probe
  }
  return null;
}
function writeCachedMe(tripIds: string[]): void {
  try {
    localStorage.setItem(ME_CACHE_KEY, JSON.stringify({ tripIds, at: Date.now() }));
  } catch {
    // private mode / storage full — optimistic render just won't kick in
  }
}
function clearCachedMe(): void {
  try {
    localStorage.removeItem(ME_CACHE_KEY);
  } catch {
    // ignore
  }
}

// "This browser has signed in before" — set on every successful probe and
// deliberately NOT cleared on 401 (the 401 is exactly when we need it): it
// distinguishes a member whose session lapsed from a genuine share-link
// guest, so the session-expired banner only shows for the former. There is
// no SPA sign-out flow that would want it cleared (sign-out lives at the
// CF Access edge).
const HAD_SESSION_KEY = 'jg.web.hadSession';
function hadSessionHere(): boolean {
  try {
    // ME_LS (getMe's warm-start cache) is the legacy evidence: it predates
    // the marker, so a session that expired before this code ever ran a
    // successful probe still gets recognized as a lapsed member (the
    // 2026-07-19 incident tab hit exactly that gap).
    return (
      localStorage.getItem(HAD_SESSION_KEY) === '1' ||
      localStorage.getItem(ME_LS) !== null
    );
  } catch {
    return false;
  }
}
function markHadSession(): void {
  try {
    localStorage.setItem(HAD_SESSION_KEY, '1');
  } catch {
    // ignore
  }
}

// Floating notice for a member whose session lapsed while a stored guest
// token kept the trip readable: without it the only tell is the tiny GUEST
// chip, and "Open the planner" silently becomes read-only "See the days"
// (the 2026-07-19 incident). Fixed bottom-center so it rides above every
// surface, including the full-bleed map routes that skip AppShell.
function SessionExpiredBanner({ onDismiss }: { onDismiss: () => void }) {
  const loc = useLocation();
  const next = encodeURIComponent(loc.pathname + loc.search);
  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 300,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 10px 10px 18px',
        borderRadius: 99,
        background: 'var(--ink)',
        color: 'var(--bg)',
        boxShadow: 'var(--shadow-pop)',
        maxWidth: 'calc(100vw - 32px)',
        fontSize: '0.9rem',
      }}
    >
      <span>Your session expired — you're viewing this as a read-only guest.</span>
      <a
        className="btn btn-primary"
        style={{ padding: '0.4rem 0.9rem', whiteSpace: 'nowrap' }}
        href={`/signin?next=${next}`}
      >
        Sign in
      </a>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--bg)',
          opacity: 0.6,
          cursor: 'pointer',
          fontSize: 18,
          lineHeight: 1,
          padding: '4px 6px',
        }}
      >
        ×
      </button>
    </div>
  );
}

// Auth probe at app load, revalidated on window focus / tab-visible and —
// while anonymous — on SPA navigation. CF Access (web) and the app-layer
// session (mobile) both resolve to an email server-side; here we just need
// to know whether /api/me succeeds. 401/403 → not signed in → SignInScreen,
// unless the location is a guest share-link surface (/g/{token}, or
// /trip/{id} covered by a stored guest token) — those render in guest mode.
// A member whose session lapsed into that guest fallback additionally gets
// the SessionExpiredBanner, and the revalidation probes promote the tab
// back to member mode without a manual reload once the session is good
// again (e.g. after running /signin in another tab).
//
// Optimistic auth: a returning member has a cached /api/me, so we start in
// 'authed' and revalidate behind the rendered app rather than holding a
// full-screen "Loading…" for the round-trip. The probe still runs every
// load; a 401/403 (expired session) clears the cache and flips to 'anon'
// → SignInScreen. First visit / signed-out (no cache) keeps the original
// blocking 'loading' state, so nothing renders speculatively for someone
// who's never authenticated here.
const REVALIDATE_MIN_GAP_MS = 5_000;
function AuthGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  const cached = useMemo(() => readCachedMe(), []);
  const [state, setState] = useState<
    'loading' | 'authed' | 'anon' | 'error'
  >(cached ? 'authed' : 'loading');
  const [message, setMessage] = useState('');
  // Trip ids the signed-in user is a real member of — a member opening
  // their own share link keeps full member capabilities.
  const [memberTripIds, setMemberTripIds] = useState<Set<string> | null>(
    cached ? new Set(cached.tripIds) : null,
  );
  // 401'd but this browser had a real session before → lapsed member, not
  // a genuine guest. Drives the banner; reset by the next successful probe.
  const [expiredSession, setExpiredSession] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const stateRef = useRef(state);
  stateRef.current = state;
  // Overlapping probes resolve out of order (focus fires during a slow
  // initial probe); only the newest one may apply state.
  const probeSeq = useRef(0);
  const lastProbeAt = useRef(0);

  const probe = useCallback(
    (kind: 'initial' | 'revalidate') => {
      const seq = ++probeSeq.current;
      lastProbeAt.current = Date.now();
      getMe()
        .then((res) => {
          if (seq !== probeSeq.current) return;
          const ids = res.trips.map((t) => t.tripId);
          writeCachedMe(ids);
          markHadSession();
          setMemberTripIds(new Set(ids));
          setExpiredSession(false);
          setBannerDismissed(false);
          setState('authed');
        })
        .catch((err) => {
          if (seq !== probeSeq.current) return;
          if (
            err instanceof ApiError &&
            (err.status === 401 || err.status === 403)
          ) {
            setExpiredSession(hadSessionHere());
            clearCachedMe();
            setMemberTripIds(null);
            setState('anon');
          } else if (kind === 'initial' && !cached) {
            // Network/5xx with no cache to fall back on — surface it. A cached
            // returning user instead keeps the optimistic 'authed' shell (works
            // offline); their route's own fetches surface any real failure.
            // Background revalidates never flip a working app to the error
            // screen (an offline alt-tab is not a sign-out).
            setMessage(err instanceof Error ? err.message : 'Failed to load.');
            setState('error');
          }
        });
    },
    [cached],
  );

  useEffect(() => {
    probe('initial');
  }, [probe]);

  // Recovery + honesty: re-probe when the tab regains focus/visibility (any
  // settled state — promotes a stale guest/sign-in tab, demotes a truly
  // expired session while a guest fallback can catch it), and on SPA
  // navigation while anonymous (clicking around a stale tab). Throttled so
  // focus flapping doesn't spam /api/me.
  useEffect(() => {
    const maybeRevalidate = () => {
      if (document.visibilityState !== 'visible') return;
      if (stateRef.current === 'loading') return;
      if (Date.now() - lastProbeAt.current < REVALIDATE_MIN_GAP_MS) return;
      probe('revalidate');
    };
    window.addEventListener('focus', maybeRevalidate);
    document.addEventListener('visibilitychange', maybeRevalidate);
    return () => {
      window.removeEventListener('focus', maybeRevalidate);
      document.removeEventListener('visibilitychange', maybeRevalidate);
    };
  }, [probe]);

  const pathname = location.pathname;
  useEffect(() => {
    if (stateRef.current !== 'anon') return;
    if (Date.now() - lastProbeAt.current < REVALIDATE_MIN_GAP_MS) return;
    probe('revalidate');
  }, [pathname, probe]);

  if (state === 'loading') {
    return (
      <AppShell>
        <p className="loading">Loading…</p>
      </AppShell>
    );
  }
  if (state === 'error') {
    return (
      <AppShell>
        <p className="error">{message}</p>
      </AppShell>
    );
  }

  // Re-derived on every navigation: stepping from /g/{token} into
  // /trip/{id} stays in guest mode; leaving for /, /new, /reservations
  // falls back to SignInScreen for anonymous visitors.
  const guest = guestSessionFor(location.pathname);
  if (state === 'anon' && !guest) return <SignInScreen />;
  const isGuest =
    guest !== null &&
    (state === 'anon' ||
      (guest.tripId !== null &&
        memberTripIds !== null &&
        !memberTripIds.has(guest.tripId)));
  return (
    <GuestContext.Provider
      value={{ isGuest, tripId: guest?.tripId ?? null, anonymous: state === 'anon' }}
    >
      {children}
      {state === 'anon' && expiredSession && !bannerDismissed && (
        <SessionExpiredBanner onDismiss={() => setBannerDismissed(true)} />
      )}
    </GuestContext.Provider>
  );
}

// Suspense fallback while a lazy route chunk downloads. Mounts the shared
// shell so the chrome stays put and only the content area swaps — same
// shape as AuthGate's loading state, so an authed cold load that lands on
// a lazy route shows one continuous "Loading…" rather than a flash of
// empty then a second flash. Map routes render their own full-bleed
// loaders once mounted, so this only covers the brief chunk fetch.
function RouteFallback() {
  return (
    <AppShell>
      <p className="loading">Loading…</p>
    </AppShell>
  );
}

export default function App() {
  const location = useLocation();
  // The shareable showcase (/s/{token}) reads its own token-scoped route
  // and needs no account: it skips the auth probe and the app chrome.
  if (location.pathname.startsWith('/s/')) {
    return (
      <ErrorBoundary key={location.pathname}>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/s/:token" element={<SharedShowcase />} />
            <Route path="/s/:token/:n" element={<SharedShowcase />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    );
  }
  return (
    <AuthGate>
      <ErrorBoundary key={location.pathname}>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<TripsList />} />
            <Route path="/g/:token" element={<GuestLanding />} />
            <Route path="/d/:tripId/:decisionId" element={<DecisionShortLink />} />
            <Route path="/new" element={<NewTripWizard />} />
            <Route path="/reservations" element={<GlobalReservations />} />
            <Route path="/dining" element={<Dining />} />
            <Route path="/lodging" element={<CorridorLodging />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/trip/:tripId" element={<Overview />} />
            <Route path="/trip/:tripId/planner" element={<Planner />} />
            <Route path="/trip/:tripId/catalog" element={<Browse />} />
            <Route path="/trip/:tripId/catalog/:type/:id" element={<CatalogDetail />} />
            <Route path="/trip/:tripId/days" element={<Days />} />
            <Route path="/trip/:tripId/meals" element={<Meals />} />
            <Route path="/trip/:tripId/experiences" element={<Experiences />} />
            <Route path="/trip/:tripId/reservations" element={<Reservations />} />
            <Route path="/trip/:tripId/budget" element={<Budget />} />
            {/* Map routes are full-bleed (no AppShell) and render their own
                loader once mounted, so they keep a null fallback — the inner
                boundary catches before the shell-based RouteFallback, avoiding
                a flash of standard chrome during the mapbox chunk fetch. */}
            {/* The old map page retired for map mode, the one web map (#604). */}
            <Route path="/trip/:tripId/map" element={<MapRedirect />} />
            <Route
              path="/trip/:tripId/map-mode"
              element={<Suspense fallback={null}><MapMode /></Suspense>}
            />
            {/* The showcase is full-bleed too: its own masthead, no app chrome. */}
            <Route path="/trip/:tripId/showcase" element={<Suspense fallback={null}><Showcase /></Suspense>} />
            <Route path="/trip/:tripId/showcase/:n" element={<Suspense fallback={null}><Showcase /></Suspense>} />
            <Route
              path="/trip/:tripId/transit"
              element={<Suspense fallback={null}><TripTransit /></Suspense>}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
      <ToastHost />
    </AuthGate>
  );
}
